import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment, type ReactNode } from 'react';
import { connect } from 'react-redux';
import { Route, Routes } from 'react-router';
import { bindActionCreators } from 'redux';
import AppContent from '/@/components/app-content';
import ExpertoFieldViewPostLoginModal from '/@/components/experto-fieldview-post-login-modal';
import Loading from '/@/components/loading';
import LocalePrefix from '/@/components/locale-prefix';
import NetworkCheck from '/@/components/network-check';
import ViewContext from '/@/components/view-context';
import { type AppViews, AppViewsContext, UnitsContext } from '/@/core/context/app';
import useUnitProvider from '/@/core/context/unit';
import { Mixpanel } from '/@/core/mixpanel';
import { UnitConverter } from '/@/core/units';
import { withRouter } from '/@/hooks/with-router';
import { configureViews, getViewByPathname, type ViewConfig } from '/@/settings/views';
import { restoreSession, setCountry } from '/@/store/session/actions';
import {
    getAppInstance,
    getSessionBrands,
    getSessionCountries,
    getSessionCountry,
    getSessionLanguage,
    getSessionUnits,
    getUser,
} from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import { excludeKeys } from '/@/utils';
import { decodeNextUrl, parseQuerystring, reverse, stringifyQuerystring, stripLocalePrefix } from '/@/utils/router';
import ErrorView from '../error-view';
import styles from './styles.module.css';

// Builds units from the same session selectors `getChildContext` uses, memoized so `useUnits`
// consumers don't re-render on every App render.
const SessionUnitsProvider = ({ children }: { children: ReactNode }) => (
    <UnitsContext.Provider value={useUnitProvider()}>{children}</UnitsContext.Provider>
);

@withRouter
@connect(
    (state) => ({
        user: getUser(state),
        units: getSessionUnits(state),
        language: getSessionLanguage(state),
        sessionBrands: getSessionBrands(state),
        sessionCountry: getSessionCountry(state),
        activeView: state.nav.activeView,
        appInstance: getAppInstance(state),
        sessionCountries: getSessionCountries(state),
    }),
    (dispatch) => bindActionCreators({ restoreSession, trackEvent, setCountry }, dispatch)
)
@autobind
export default class App extends Component {
    declare appViews: AppViews;

    static childContextTypes = {
        units: PropTypes.object,
        history: PropTypes.object,
        getActiveView: PropTypes.func,
        viewsByName: PropTypes.object,
    };

    state = {
        loading: true,
        overflow: 'hidden',
        showMenu: false,
    };

    getChildContext() {
        return this.state.loading
            ? {}
            : {
                  units: new UnitConverter({
                      ...this.props.units,
                      language: this.props.language,
                      brands: this.props.sessionBrands,
                  }),
                  history: this.props.history,
                  getActiveView: this.getActiveView,
                  viewsByName: this.viewsByName,
              };
    }

    constructor(props) {
        super(props);

        const { views, viewsByName } = configureViews();

        this.views = views;
        this.viewsByName = viewsByName;
        this.appViews = { getActiveView: this.getActiveView, viewsByName };
    }

    UNSAFE_componentWillMount() {
        this.initialize();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { user, location, appInstance } = nextProps;
        if (this.props.location !== location || this.props.user !== user || this.props.appInstance !== appInstance) {
            this.validate(location, user);
        }
    }

    async initialize() {
        this.setState({ loading: true });

        const params = parseQuerystring(this.props.location.search);
        const { user } = (await this.props.restoreSession({ params })) || {};
        const savedCountry = JSON.parse(localStorage.getItem('country-setted'));

        const qparams = new URLSearchParams(window.location.search);
        if (qparams.get('country')) {
            const country = this.props.sessionCountries.find((c) => c.code === qparams.get('country'));
            if (country) await this.props.setCountry({ country: country.id, interactive: false });
        } else if (!user && this.props.sessionCountries.some((c) => c.id === savedCountry)) {
            await this.props.setCountry({ country: savedCountry, interactive: false });
        }

        Mixpanel.opt_in_tracking();

        this.validate(this.props.location, user);
        this.cleanQuerystring(params);
        this.setState({ loading: false });
    }

    /** Experto FieldView: show blocking modal instead of sending user into the app after login. */
    shouldHoldFieldviewModalAfterLogin(user) {
        return Boolean(user && this.props.appInstance?.enableFieldviewBlockingModal && !user.validated_type);
    }

    validate(location, user) {
        const redirect = this.getRedirect(location, user);
        if (!redirect) return;

        const destPath = redirect.split('?')[0];
        const curPath = location.pathname;
        const norm = (p) => (p || '/').replace(/\/+$/, '') || '/';
        if (norm(destPath) === norm(curPath)) return;

        this.props.history(redirect, { replace: true });
    }

    cleanQuerystring(params) {
        const exclude = ['next', 'conversionKey', 'conversionToken'];
        if (!exclude.some((key) => key in params)) return;

        this.props.history(
            {
                pathname: this.props.location.pathname,
                search: stringifyQuerystring(excludeKeys(params, exclude)),
            },
            { replace: true }
        );
    }

    getRedirect(location, user) {
        // `getViewByPathname` already strips `/:language`; do not strip twice or `matchPath` fails.
        const view = getViewByPathname(location.pathname, this.views);

        if (!view) return;

        if (view.type === 'public') {
            return;
        }

        if (user) {
            // Block all navigation while the FieldView blocking modal is active (catalog stays public).
            if (
                this.shouldHoldFieldviewModalAfterLogin(user) &&
                !['auth', 'public', 'email-verified', 'catalog'].includes(view.type)
            ) {
                return reverse('Login');
            }
            // Redirect auth → app after login, unless FieldView migration modal should stay up (no navigation).
            if (view.type === 'auth' && user.type) {
                if (this.shouldHoldFieldviewModalAfterLogin(user)) {
                    return;
                }
                return decodeNextUrl(location);
            }
        } else if (!['auth', 'catalog', 'email-verified'].includes(view.type)) {
            // Redirect non-authentication views to login so the user can authenticate.
            const next = `${stripLocalePrefix(location.pathname)}${location.search}`;
            return `${reverse('Login')}?next=${encodeURIComponent(next)}`;
        }
    }

    getActiveView() {
        const { activeView } = this.props;
        return (activeView && this.viewsByName[activeView.name]) || {};
    }

    renderLayout() {
        const { sessionCountry, language } = this.props;
        if (this.state.loading || !sessionCountry) return <Loading />;

        return (
            <Fragment>
                <LocalePrefix language={language}>
                    <Routes>
                        {(this.views as ViewConfig[]).map((view) => (
                            <Route
                                key={view.name}
                                path={'/:language'.concat(view.path)}
                                element={
                                    <AppContent
                                        hideAskAnExpert={view.hideAskAnExpert}
                                        appSidebarTabKey={view.appSidebarTabKey}
                                        // The catalog is public: never render the app navbar there.
                                        hideSidebar={view.hideSidebar || view.type === 'catalog'}
                                    >
                                        <ViewContext
                                            {...view}
                                            brands={this.props.sessionBrands}
                                            viewsByName={this.viewsByName}
                                            component={view.component}
                                            location={this.props.location}
                                            key={view.name}
                                        />
                                    </AppContent>
                                }
                            />
                        ))}
                        <Route path='*' element={<ErrorView />} />
                    </Routes>
                </LocalePrefix>
                <ExpertoFieldViewPostLoginModal />
            </Fragment>
        );
    }

    render() {
        return (
            <div className={styles.root} style={{ overflow: this.state.overflow }}>
                <NetworkCheck />
                <SessionUnitsProvider>
                    <AppViewsContext.Provider value={this.appViews}>{this.renderLayout()}</AppViewsContext.Provider>
                </SessionUnitsProvider>
            </div>
        );
    }
}
